import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, Edit2, Loader2, User, Building2, Calendar,
  FileText, MessageSquare, FolderOpen, AlertCircle, Scale, ClipboardList,
  Plus, Download, Trash2, X, Search, FolderTree
} from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import CaseNotesTimeline from '@/components/CaseNotesTimeline';
import { confirmToast } from '@/utils/confirmToast';

const STATUS_COLORS = {
  'Active': 'bg-green-100 text-green-700 border-green-300',
  'Pending': 'bg-yellow-100 text-yellow-700 border-yellow-300',
  'Closed': 'bg-slate-100 text-slate-600 border-slate-300',
  'On Hold': 'bg-orange-100 text-orange-700 border-orange-300'
};

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all'); // 'all', 'case_specific', 'generic'
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchCaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }
    try {
      const response = await fetch(`${API}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (userData.app_mode !== 'crm') {
          toast.error('CRM access required');
          navigate('/dashboard');
        }
      } else {
        navigate('/auth');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCaseData = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/cases/${caseId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        setCaseData(await response.json());
      } else {
        toast.error('Case not found');
        navigate('/cases');
      }
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/cases');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseDocuments = async () => {
    const token = localStorage.getItem('token');
    setLoadingDocuments(true);
    try {
      const response = await fetch(`${API}/cases/${caseId}/documents`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      if (response.ok) {
        setDocuments(await response.json());
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const fetchTemplates = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/templates`, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      if (response.ok) {
        setTemplates(await response.json());
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  // Load documents when switching to documents tab
  useEffect(() => {
    if (activeTab === 'documents') {
      fetchCaseDocuments();
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, caseId]);

  const handleRunTemplate = (template) => {
    // Navigate to template run page with case context
    navigate(`/templates/${template.template_id}/run?caseId=${caseId}`);
    setShowTemplateModal(false);
  };

  const handleDownloadDocument = async (doc, format = 'docx') => {
    const token = localStorage.getItem('token');
    try {
      // Use the correct endpoint based on document type
      const url = `${API}/cases/${caseId}/documents/${doc.document_id}/download?format=${format}`;
      
      const response = await fetch(url, { 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      
      if (!response.ok) throw new Error('Failed to download');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = downloadUrl;
      a.download = `${doc.document_name}.${format}`;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      window.document.body.removeChild(a);
      toast.success(`Downloaded ${doc.document_name}.${format}`);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = (doc) => {
    confirmToast(
      `Are you sure you want to delete "${doc.document_name}"?`,
      async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await fetch(
            `${API}/cases/${caseId}/documents/${doc.document_id}`,
            { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
          );
          if (response.ok) {
            toast.success('Document deleted');
            fetchCaseDocuments();
          } else {
            toast.error('Failed to delete document');
          }
        } catch (error) {
          toast.error('Failed to delete document');
        }
      },
      { title: 'Delete Document' }
    );
  };

  // Filter templates based on search and filter
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(templateSearchQuery.toLowerCase());
    const matchesFilter = templateFilter === 'all' || 
      (templateFilter === 'case_specific' && t.is_case_specific) ||
      (templateFilter === 'generic' && !t.is_case_specific);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const tabs = [
    { id: 'details', label: 'Case Details', icon: Scale },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'notes', label: 'Notes', icon: MessageSquare },
    { id: 'forms', label: 'Forms', icon: FolderOpen },
    { id: 'calendar', label: 'Calendar', icon: Calendar }
  ];

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar user={user} />
      <div className="flex-1 flex flex-col lg:ml-64">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <Button variant="ghost" onClick={() => navigate('/cases')} className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Cases
            </Button>
            
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-2xl p-8 mb-6 shadow-xl">
              <div className="flex justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold mb-2">{caseData?.case_title}</h1>
                  <p className="text-violet-100">{caseData?.court_case_number || caseData?.internal_case_number}</p>
                </div>
                <div className="flex gap-3">
                  <span className={`px-4 py-2 rounded-full border-2 ${STATUS_COLORS[caseData?.status]}`}>{caseData?.status}</span>
                  <Button 
                    onClick={() => navigate(`/forms?caseId=${caseId}`)} 
                    className="bg-white/10 border border-white/30 text-white hover:bg-white/20"
                  >
                    <FolderTree className="w-4 h-4 mr-2" />
                    Generate Form
                  </Button>
                  <Button onClick={() => navigate(`/cases/edit/${caseId}`)} className="bg-white/10 border border-white/30 text-white hover:bg-white/20">
                    <Edit2 className="w-4 h-4 mr-2" />Edit
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <p className="text-violet-100 text-xs mb-1">Client</p>
                  <p className="font-semibold">{caseData?.client?.full_name || caseData?.client_name}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <p className="text-violet-100 text-xs mb-1">Attorney</p>
                  <p className="font-semibold">{caseData?.attorney?.full_name || caseData?.attorney_name}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <p className="text-violet-100 text-xs mb-1">Next Hearing</p>
                  <p className="font-semibold">{caseData?.next_hearing_date ? new Date(caseData.next_hearing_date).toLocaleDateString() : 'Not set'}</p>
                </div>
                <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <p className="text-violet-100 text-xs mb-1">Priority</p>
                  <p className="font-semibold">{caseData?.priority}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl border mb-6">
              <div className="flex border-b overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-6 py-4 border-b-2 transition whitespace-nowrap ${activeTab === tab.id ? 'border-violet-600 text-violet-600 font-semibold bg-violet-50/50' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}>
                      <Icon className="w-4 h-4" />{tab.label}
                    </button>
                  );
                })}
              </div>
              <div className="p-8 min-h-[500px]">
                {activeTab === 'details' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6">Case Information</h2>
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="font-semibold mb-4">Parties</h3>
                        <div className="space-y-3">
                          <div><p className="text-xs text-slate-600">Petitioner</p><p className="font-medium">{caseData?.petitioner_name}</p></div>
                          <div><p className="text-xs text-slate-600">Respondent</p><p className="font-medium">{caseData?.respondent_name}</p></div>
                          {caseData?.opposing_counsel && <div><p className="text-xs text-slate-600">Opposing Counsel</p><p className="font-medium">{caseData.opposing_counsel.first_name} {caseData.opposing_counsel.last_name}</p></div>}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="font-semibold mb-4">Court</h3>
                        <div className="space-y-2 text-sm">
                          <p>{caseData?.court_name}</p>
                          <p>{caseData?.county} County</p>
                          {caseData?.judge_name && <p>Judge: {caseData.judge_name}</p>}
                        </div>
                      </div>
                    </div>
                    
                    {/* Children Section */}
                    {caseData?.client?.children && caseData.client.children.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 mb-6">
                        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                          <User className="w-5 h-5 text-blue-600" />
                          Children Involved ({caseData.client.children.length})
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {caseData.client.children.map((child, idx) => (
                            <div key={child.child_id || idx} className="bg-white rounded-lg p-4 border border-blue-200">
                              <p className="font-semibold text-slate-900 mb-2">{child.full_name}</p>
                              {child.date_of_birth && (
                                <p className="text-xs text-slate-600 mb-1">
                                  DOB: {new Date(child.date_of_birth).toLocaleDateString()}
                                </p>
                              )}
                              {child.gender && (
                                <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                  {child.gender}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Case Types */}
                    <div className="bg-slate-50 rounded-xl p-6">
                      <h3 className="font-semibold mb-4">Case Types</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {caseData?.case_types?.map((type, idx) => (
                          <span key={idx} className="px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 text-sm font-medium">
                            {type}
                          </span>
                        ))}
                      </div>
                      {caseData?.description && (
                        <div className="pt-4 border-t border-slate-200">
                          <p className="text-xs text-slate-600 mb-1">Description</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{caseData.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {activeTab === 'intake' && (
                  <div className="text-center py-20">
                    <ClipboardList className="w-20 h-20 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Intake Sheets</h3>
                    <p className="text-slate-500 mb-6">
                      Client intake forms and questionnaires for this case
                    </p>
                    <p className="text-sm text-slate-400">
                      Coming soon: Initial client questionnaire, case intake forms, client information sheets
                    </p>
                  </div>
                )}
                {activeTab === 'documents' && (
                  <div>
                    {/* Documents Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Case Documents</h3>
                        <p className="text-sm text-slate-500">Documents generated for this case</p>
                      </div>
                      <Button
                        onClick={() => navigate(`/select-template?caseId=${caseId}`)}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="add-document-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Document
                      </Button>
                    </div>
                    
                    {/* Documents List */}
                    {loadingDocuments ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
                      </div>
                    ) : documents.filter(d => d.document_type !== 'form').length === 0 ? (
                      <div className="text-center py-16 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                        <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                        <h4 className="text-lg font-semibold text-slate-700 mb-2">No Template Documents Yet</h4>
                        <p className="text-slate-500 mb-4">Generate documents from templates with auto-filled case data</p>
                        <Button
                          onClick={() => navigate(`/select-template?caseId=${caseId}`)}
                          variant="outline"
                          className="border-violet-300 text-violet-600 hover:bg-violet-50"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create First Document
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full" data-testid="documents-table">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Document Name</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Template</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Format</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                              <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {documents.filter(d => d.document_type !== 'form').map((doc) => (
                              <tr key={doc.document_id} className="hover:bg-slate-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-violet-500" />
                                    <span className="font-medium text-slate-900">{doc.document_name}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">{doc.template_name}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    doc.file_format === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {doc.file_format?.toUpperCase() || 'DOCX'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-500">
                                  {new Date(doc.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadDocument(doc, 'docx')}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      data-testid={`download-docx-${doc.document_id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadDocument(doc, 'pdf')}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`download-pdf-${doc.document_id}`}
                                    >
                                      PDF
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteDocument(doc)}
                                      className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                                      data-testid={`delete-doc-${doc.document_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Template Selection Modal */}
                    {showTemplateModal && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTemplateModal(false)}>
                        <div 
                          className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden shadow-xl"
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">Select Template</h3>
                              <p className="text-sm text-slate-500">Choose a template to generate a document</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setShowTemplateModal(false)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Search and Filter */}
                          <div className="px-6 py-4 border-b border-slate-100 space-y-3">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                placeholder="Search templates..."
                                value={templateSearchQuery}
                                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                                data-testid="template-search-input"
                              />
                            </div>
                            <div className="flex gap-2">
                              {[
                                { value: 'all', label: 'All Templates' },
                                { value: 'case_specific', label: 'Case-Specific' },
                                { value: 'generic', label: 'Generic' }
                              ].map(filter => (
                                <button
                                  key={filter.value}
                                  onClick={() => setTemplateFilter(filter.value)}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    templateFilter === filter.value
                                      ? 'bg-violet-100 text-violet-700'
                                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                  }`}
                                >
                                  {filter.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          
                          {/* Templates List */}
                          <div className="overflow-y-auto max-h-[400px]">
                            {filteredTemplates.length === 0 ? (
                              <div className="p-6 text-center text-slate-500">
                                No templates found
                              </div>
                            ) : (
                              <div className="divide-y divide-slate-100">
                                {filteredTemplates.map(template => (
                                  <button
                                    key={template.template_id}
                                    onClick={() => handleRunTemplate(template)}
                                    className="w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors flex items-center justify-between"
                                    data-testid={`select-template-${template.template_id}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-slate-900 truncate">{template.name}</span>
                                        {template.is_case_specific && (
                                          <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-medium">
                                            Case-Specific
                                          </span>
                                        )}
                                      </div>
                                      {template.description && (
                                        <p className="text-sm text-slate-500 truncate">{template.description}</p>
                                      )}
                                    </div>
                                    <div className="ml-4 text-violet-600">
                                      <ArrowLeft className="w-4 h-4 rotate-180" />
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'notes' && (
                  <CaseNotesTimeline caseId={caseId} currentUser={user} />
                )}
                {activeTab === 'forms' && (
                  <div>
                    {/* Forms Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">California Law Forms</h3>
                        <p className="text-sm text-slate-500">Forms generated from Forms Library</p>
                      </div>
                      <Button
                        onClick={() => navigate(`/forms?caseId=${caseId}`)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Generate Form
                      </Button>
                    </div>
                    
                    {/* Forms List */}
                    {loadingDocuments ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
                      </div>
                    ) : documents.filter(d => d.document_type === 'form').length === 0 ? (
                      <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center">
                        <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium mb-1">No forms generated yet</p>
                        <p className="text-sm text-slate-400 mb-4">Generate California law forms with auto-filled case data</p>
                        <Button
                          onClick={() => navigate(`/forms?caseId=${caseId}`)}
                          variant="outline"
                          className="mx-auto border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Generate First Form
                        </Button>
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Form Name</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Form Code</th>
                              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                              <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {documents.filter(d => d.document_type === 'form').map((doc) => (
                              <tr key={doc.document_id} className="hover:bg-slate-50">
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-500" />
                                    <span className="font-medium text-slate-900">{doc.document_name}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                                    {doc.form_code}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-500">
                                  {new Date(doc.created_at).toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownloadDocument(doc, 'pdf')}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      PDF
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteDocument(doc)}
                                      className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
                {activeTab === 'calendar' && (
                  <div className="text-center py-20">
                    <Calendar className="w-20 h-20 mx-auto mb-4 text-slate-300" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Calendar & Deadlines</h3>
                    <p className="text-slate-500 mb-6">
                      Important dates, hearings, and deadlines for this case
                    </p>
                    <p className="text-sm text-slate-400">
                      Coming soon: Deadline tracking, hearing reminders, court calendar sync
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
